/**
 * The browser's microphone level.
 *
 * Taken from the recorder's *own* capture: the stream `expo-audio` opened is
 * borrowed for the length of the take (see `flashbookMicTap`), its samples are
 * read through an `AnalyserNode`, and only this meter's own audio graph is closed
 * afterwards. The recorder keeps its microphone.
 *
 * It used to open a second `getUserMedia` instead, which is a second device — and
 * WebKit gives an app one, so the meter was switched off entirely on Safari and on
 * iOS in every browser. That was the wrong trade twice over: the browser this app
 * is most read in on an iPhone lost the screen that answers *is it hearing me?*,
 * and the level it did show elsewhere was a parallel view of the input rather than
 * the input being recorded.
 *
 * Where the borrow does not happen — a build with no `mediaDevices`, a recorder
 * that opened its stream before the meter was armed — the meter says it cannot
 * measure rather than moving on its own.
 */
import {levelFromRms, type MicLevelSource} from '#/lib/flashbookMicLevel'
import {peekTappedStream} from '#/lib/flashbookMicTap'

/** How often a level is taken. ~16 Hz: faster than an eye, slower than a waste. */
const SAMPLE_MS = 60

/**
 * How much of the window is above the meter's floor.
 *
 * `levelFromRms` is fed the raw RMS, and the shaping there decides how tall a
 * voice is; this scale only undoes the headroom a browser leaves on quiet input.
 * A voice held at a phone's distance lands around 1.6×.
 */
const INPUT_GAIN = 1.6

function audioContextCtor(): typeof AudioContext | undefined {
  if (typeof window === 'undefined') return undefined
  return (
    window.AudioContext ??
    (window as {webkitAudioContext?: typeof AudioContext}).webkitAudioContext
  )
}

function canMeasure(): boolean {
  if (typeof window === 'undefined') return false
  return !!peekTappedStream() && typeof audioContextCtor() === 'function'
}

/**
 * The device's reader is accepted and ignored: a browser measures the samples
 * itself, and there is no recorder metering to read. Kept in the signature so both
 * platforms are the same shape to the call site.
 */
export function createPlatformMicLevelSource(
  _readDbfs?: () => number | null | undefined,
): MicLevelSource {
  return {
    supported: canMeasure(),

    start(onLevel) {
      const media = peekTappedStream()
      const Ctor = audioContextCtor()
      if (!media || typeof Ctor !== 'function') return () => {}

      let closed = false
      let context: AudioContext | null = null
      let timer: ReturnType<typeof setInterval> | null = null

      /**
       * Close *this* meter, and nothing else.
       *
       * The tracks are deliberately left alone: they are the recorder's capture,
       * still running, and stopping them here would end the take the creator is in
       * the middle of. Only the graph built on top of them goes.
       */
      const release = () => {
        if (closed) return
        closed = true
        if (timer) clearInterval(timer)
        timer = null
        void context?.close().catch(() => {})
        context = null
      }

      try {
        context = new Ctor()
        const source = context.createMediaStreamSource(media)
        const analyser = context.createAnalyser()
        // Small window: the meter wants *now*, not an average of the last
        // second — a syllable that lasts 80ms should still be visible.
        analyser.fftSize = 1024
        analyser.smoothingTimeConstant = 0.4
        source.connect(analyser)
        const samples = new Float32Array(analyser.fftSize)

        timer = setInterval(() => {
          if (closed) return
          analyser.getFloatTimeDomainData(samples)
          let sum = 0
          for (let i = 0; i < samples.length; i++)
            sum += samples[i] * samples[i]
          const rms = Math.sqrt(sum / samples.length) * INPUT_GAIN
          onLevel(levelFromRms(rms))
        }, SAMPLE_MS)
      } catch {
        // A stream that will not feed a graph — already ended, or a browser that
        // refuses one on a capture from elsewhere — leaves the card its clock.
        release()
      }

      return release
    },
  }
}
